/**
 * Settings API Service
 *
 * Provides data access for the Settings feature module.
 */
import axios from 'axios';
import i18next from 'i18next';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES, config } from '@/core/config';
import { toAppProfileSettingSlug } from '@tms-phoenix/tms-types';
import type {
  Device,
  ApprovalComplianceSetting,
  BridgelinkAssetsResponse,
  BridgelinkStatusResponse,
  AppProfileSettingType,
  AppProfileSettingResponse,
  AppProfileSettingColumnDefinition,
  AppProfileSettingEffectiveResponse,
} from '@tms-phoenix/tms-types';
import type {
  AllergenManagementEntry,
  AllergenAction,
  LocalizationFormData,
  MilkTypeEntry,
  HospitalConfigFormData,
  FacilityEntry,
  UnitConfigEntry,
  ExpiryRuleEntry,
  RolePermissionEntry,
  ApprovalComplianceFormData,
  BloodTestEntry,
  ValidityUnit,
  MilkFormulaStorageFormData,
  StorageLocationEntry,
  ContactDetailsFormData,
} from '../types';

// ---------------------------------------------------------------------------
// Allergen Management (Step 1) — hospital-allergen-config
// ---------------------------------------------------------------------------

const ALLERGEN_PREFIX = API_ROUTES.ALLERGENS;
const api = getApiClient();

type SystemAction = 'NONE' | 'SOFT_STOP' | 'HARD_STOP';

type AllergenConfigRow = {
  config_id: string;
  allergen_name: string;
  severity: string | null;
  system_action: SystemAction | null;
};

function toAllergenAction(systemAction: SystemAction | null): AllergenAction | null {
  if (systemAction === 'HARD_STOP') return 'Hard Stop (Stop)';
  if (systemAction === 'SOFT_STOP') return 'Soft Stop (Warning)';
  if (systemAction === 'NONE') return 'No Action';
  return null;
}

function toSystemAction(action: AllergenAction): SystemAction {
  if (action === 'Hard Stop (Stop)') return 'HARD_STOP';
  if (action === 'Soft Stop (Warning)') return 'SOFT_STOP';
  return 'NONE';
}

/**
 * Fetch allergen management configuration entries for the current hospital.
 * Calls GET /api/v1/hospitalAllergenConfig/
 */
export async function fetchAllergenManagement(): Promise<AllergenManagementEntry[]> {
  const response = await api.get<AllergenConfigRow[]>(
    `${ALLERGEN_PREFIX}/hospitalAllergenConfig/`,
  );
  return response.data.map((row) => ({
    id: row.config_id,
    allergenName: row.allergen_name,
    allergenCategory: '',
    severity: (row.severity ?? 'Moderate') as AllergenManagementEntry['severity'],
    action: toAllergenAction(row.system_action),
  }));
}

/**
 * Update the system_action for a specific allergen config entry.
 * Calls PATCH /api/v1/hospitalAllergenConfig/{configId}
 */
export async function updateAllergenAction(
  id: string,
  action: AllergenAction,
): Promise<void> {
  await api.patch(`${ALLERGEN_PREFIX}/hospitalAllergenConfig/${id}`, {
    system_action: toSystemAction(action),
  });
}

/**
 * Check whether the hospital allergen configuration has been set up.
 * Returns true if every row has a non-null system_action (onboarded),
 * or false if any row still has system_action === null (first-time setup).
 * Calls GET /api/v1/hospitalAllergenConfig/
 */
export async function checkAllergenSetup(): Promise<boolean> {
  const response = await api.get<AllergenConfigRow[]>(
    `${ALLERGEN_PREFIX}/hospitalAllergenConfig/`,
  );
  return response.data.every((row) => row.system_action !== null);
}

// ---------------------------------------------------------------------------
// Localization (Step 8) — Batch Standards + Regional Identity
// ---------------------------------------------------------------------------

/**
 * Mock localization data — mirrors the Figma "Localization" step (Step 8).
 * English (US) locale, DD/MM/YYYY format, and sample translation entries.
 * Replace Promise.resolve() with: api.get(`${API_ROUTES.SETTINGS}/localization`)
 */
let LOCALIZATION_MOCK: LocalizationFormData = {
  regionalIdentity: {
    defaultLanguage: 'English (US)',
    dateFormat: 'DD/MM/YYYY',
  },
  translations: [
    { id: 'tr-1', labelKey: 'Patient', translation: 'Patienten' },
  ],
};

/**
 * Fetch localization settings for the current hospital.
 * TODO: replace with api.get(`${API_ROUTES.SETTINGS}/localization`)
 */
export async function fetchLocalizationSettings(): Promise<LocalizationFormData> {
  // TODO: replace with: api.get(`${API_ROUTES.SETTINGS}/localization`)
  return Promise.resolve({
    regionalIdentity: { ...LOCALIZATION_MOCK.regionalIdentity },
    translations: LOCALIZATION_MOCK.translations.map((t) => ({ ...t })),
  });
}

/**
 * Persist localization settings for the current hospital.
 * TODO: replace with api.put(`${API_ROUTES.SETTINGS}/localization`, settings)
 */
export async function saveLocalizationSettings(settings: LocalizationFormData): Promise<void> {
  LOCALIZATION_MOCK = {
    regionalIdentity: { ...settings.regionalIdentity },
    translations: settings.translations.map((t) => ({ ...t })),
  };
  return Promise.resolve();
}

// ---------------------------------------------------------------------------
// Milk Type (Step 2)
// ---------------------------------------------------------------------------

type MilkTypeRow = {
  milk_type_id: string;
  type_code: string;
  type_name: string;
  caloric_value_kcal_per_ml: number | null;
  description: string | null;
  severity_hours: number | null;
  is_active: boolean;
};

export async function fetchMilkTypes(): Promise<MilkTypeEntry[]> {
  const response = await api.get<MilkTypeRow[]>(`${API_ROUTES.SETTINGS}/milkTypes/`);
  return response.data.map((row) => ({
    id: row.milk_type_id,
    typeCode: row.type_code,
    milkType: row.type_name,
    caloricValue: row.caloric_value_kcal_per_ml ?? 0,
    description: row.description ?? '',
    severity: row.severity_hours ?? undefined,
    status: row.is_active ? 'Active' : 'Inactive',
  }));
}

export async function updateMilkTypeStatus(
  id: string,
  status: MilkTypeEntry['status'],
): Promise<void> {
  await api.patch(`${API_ROUTES.SETTINGS}/milkTypes/${id}`, {
    is_active: status === 'Active',
  });
}

export async function updateMilkTypeSeverity(id: string, severity: number): Promise<void> {
  await api.patch(`${API_ROUTES.SETTINGS}/milkTypes/${id}`, {
    severity_hours: severity,
  });
}

// ---------------------------------------------------------------------------
// Hospital Configuration (Step 3)
// ---------------------------------------------------------------------------

let HOSPITAL_CONFIG_MOCK: HospitalConfigFormData = {
  levels: [
    { id: 'lv-1', type: 'Department' },
    { id: 'lv-2', type: 'Sub-Department' },
    { id: 'lv-3', type: 'Unit' },
    { id: 'lv-4', type: 'Other' },
  ],
  rows: [
    { Department: 'Pediatrics', 'Sub-Department': 'General', Unit: 'UNIT A', Other: '' },
    { Department: 'Pediatrics', 'Sub-Department': 'ICU', Unit: 'UNIT B', Other: '' },
    { Department: 'Pediatrics', 'Sub-Department': 'Emergency', Unit: 'UNIT C', Other: '' },
    { Department: 'Pediatrics', 'Sub-Department': 'General', Unit: 'UNIT D', Other: '' },
    { Department: 'Pediatrics', 'Sub-Department': 'ICU', Unit: 'UNIT A', Other: '' },
  ],
};

export async function fetchHospitalConfig(): Promise<HospitalConfigFormData> {
  return Promise.resolve({
    levels: HOSPITAL_CONFIG_MOCK.levels.map((l) => ({ ...l })),
    rows: HOSPITAL_CONFIG_MOCK.rows.map((r) => ({ ...r })),
  });
}

export async function saveHospitalConfig(data: HospitalConfigFormData): Promise<void> {
  HOSPITAL_CONFIG_MOCK = {
    levels: data.levels.map((l) => ({ ...l })),
    rows: data.rows.map((r) => ({ ...r })),
  };
  return Promise.resolve();
}

// ---------------------------------------------------------------------------
// Hospital Configuration — Facilities (Step 3) — tms-api-facilities :5003
// ---------------------------------------------------------------------------

const FAC_BASE = API_ROUTES.FACILITIES;

type FacilityApiRow = {
  facility_id: string;
  enterprise_id: string;
  facility_code: string;
  facility_name: string;
  facility_type: string;
  floor_level?: string | null;
};

type CareUnitApiRow = {
  care_unit_id: string;
  facility_name: string;
  unit_code: string;
  unit_name: string;
  unit_type: string;
  unit_level: number | null;
  bed_capacity: number;
};

function toFacilityEntry(row: FacilityApiRow): FacilityEntry {
  return {
    id: row.facility_id,
    facilityName: row.facility_name,
    facilityCode: row.facility_code,
    facilityType: row.facility_type,
    ...(row.floor_level ? { floorLevel: row.floor_level } : {}),
  };
}

function toCareUnitEntry(row: CareUnitApiRow): UnitConfigEntry {
  return {
    id: row.care_unit_id,
    facilityName: row.facility_name,
    unitName: row.unit_name,
    unitCode: row.unit_code,
    unitType: row.unit_type,
    unitLevel: row.unit_level ?? 0,
    bedCapacity: row.bed_capacity,
  };
}

function requireHospitalId(): string {
  if (!config.hospitalId) throw new Error('VITE_HOSPITAL_ID is not configured');
  return config.hospitalId;
}

function requireCreateIds() {
  const { hospitalId, tenantId } = config;
  if (!hospitalId) throw new Error('VITE_HOSPITAL_ID is not configured');
  if (!tenantId) throw new Error('VITE_TENANT_ID is not configured');
  return { hospitalId, tenantId };
}

export async function fetchFacilities(): Promise<FacilityEntry[]> {
  const response = await api.get<FacilityApiRow[]>(`${FAC_BASE}/locations/`);
  return response.data.map(toFacilityEntry);
}

export async function addFacility(entry: Omit<FacilityEntry, 'id'>): Promise<FacilityEntry> {
  const { hospitalId, tenantId } = requireCreateIds();
  const response = await api.post<FacilityApiRow>(`${FAC_BASE}/locations/`, {
    hospitalId,
    tenantId,
    facilityCode: entry.facilityCode,
    facilityName: entry.facilityName,
    facilityType: entry.facilityType,
    ...(entry.floorLevel ? { floorLevel: entry.floorLevel } : {}),
  });
  return toFacilityEntry(response.data);
}

export async function updateFacility(entry: FacilityEntry): Promise<void> {
  await api.put(`${FAC_BASE}/locations/${entry.id}`, {
    facilityCode: entry.facilityCode,
    facilityName: entry.facilityName,
    facilityType: entry.facilityType,
    ...(entry.floorLevel ? { floorLevel: entry.floorLevel } : {}),
  });
}

// ---------------------------------------------------------------------------
// Hospital Configuration — Unit Config (Step 3) — tms-api-facilities :5003
// ---------------------------------------------------------------------------

export async function fetchUnitConfigs(): Promise<UnitConfigEntry[]> {
  const response = await api.get<unknown>(`${FAC_BASE}/careUnits/`);
  const payload = response.data;
  const rows: CareUnitApiRow[] = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as Record<string, unknown>)?.data)
      ? (payload as Record<string, unknown>).data as CareUnitApiRow[]
      : [];
  return rows.map(toCareUnitEntry);
}

/**
 * Create a new care unit.
 * @param entry   - unit fields from the form
 * @param facilityId - the facility_id UUID (resolved by the caller)
 */
export async function addUnitConfig(
  entry: Omit<UnitConfigEntry, 'id'>,
  facilityId: string,
): Promise<UnitConfigEntry> {
  const { hospitalId, tenantId } = requireCreateIds();
  const response = await api.post<CareUnitApiRow>(`${FAC_BASE}/careUnits/`, {
    facilityId,
    hospitalId,
    tenantId,
    unitCode: entry.unitCode,
    unitName: entry.unitName,
    unitType: entry.unitType,
    // unitLevel is only valid (and required) for NICU units per the API schema
    ...(entry.unitType === 'NICU' && (entry.unitLevel ?? 0) > 0 ? { unitLevel: entry.unitLevel } : {}),
    bedCapacity: entry.bedCapacity,
  });
  return toCareUnitEntry(response.data);
}

export async function updateUnitConfig(entry: UnitConfigEntry): Promise<void> {
  await api.put(`${FAC_BASE}/careUnits/${entry.id}`, {
    unitCode: entry.unitCode,
    unitName: entry.unitName,
    unitType: entry.unitType,
    // unitLevel is only valid (and required) for NICU units per the API schema
    ...(entry.unitType === 'NICU' && (entry.unitLevel ?? 0) > 0 ? { unitLevel: entry.unitLevel } : { unitLevel: null }),
    bedCapacity: entry.bedCapacity,
  });
}

type ExpiryRuleRow = {
  rule_id: string;
  category: string;
  milk_type_label: string | null;
  storage_condition: string | null;
  expiry_duration: number;
  unit: string;
  feed_type: string | null;
  expiry_type: string | null;
  form_factor?: string | null;
  state?: string | null;
  is_active: boolean;
  created_by: string;
};

/**
 * Maps a raw `milk_expiry_rules.category` value to a view-model category.
 * Tolerant of both the legacy create/update codes (MOTHER/DONOR) and the
 * reference-data codes used by the reseeded rule set (MOM/Donor/Donor
 * Pasteurised/FEED) so display stays correct regardless of which spelling
 * a given row currently has.
 */
function toViewCategory(category: string): ExpiryRuleEntry['category'] {
  switch (category) {
    case 'DONOR':
    case 'Donor':
      return 'Donor Milk';
    case 'Donor Pasteurised':
      return 'Donor Pasteurised Milk';
    case 'FORMULA':
      return 'Formula';
    case 'FEED':
      return 'Feed';
    default:
      return 'Mother Milk';
  }
}

function toApiCategory(category: ExpiryRuleEntry['category']): string {
  if (category === 'Donor Milk') return 'DONOR';
  if (category === 'Donor Pasteurised Milk') return 'Donor Pasteurised';
  if (category === 'Formula') return 'FORMULA';
  if (category === 'Feed') return 'FEED';
  return 'MOTHER';
}

const MILK_TYPE_LABEL_MAP: Record<string, string> = {
  FRESH: 'Fresh',
  FROZEN: 'Frozen',
  THAWED: 'Thawed',
  PASTEURISED: 'Pasteurised',
  OPEN: 'Open',
};

const MILK_TYPE_LABEL_REVERSE: Record<string, string> = {
  'Fresh': 'FRESH',
  'Frozen': 'FROZEN',
  'Thawed': 'THAWED',
  'Pasteurised': 'PASTEURISED',
  'Fresh (Post-Pasteurization)': 'PASTEURISED',
  'Frozen (Post-Pasteurization)': 'FROZEN',
  'Open': 'OPEN',
};

const FEED_TYPE_MAP: Record<string, string> = {
  REFRIGERATED: 'Plain',
  FROZEN: 'Mixed / Fortified',
};

const _FEED_TYPE_REVERSE: Record<string, string> = {
  'Plain': 'REFRIGERATED',
  'Mixed / Fortified': 'FROZEN',
};

// feed_type field (PLAIN/FORTIFIED) for FEED expiry rules
const FEED_EXPIRY_TYPE_MAP: Record<string, string> = {
  PLAIN: 'Plain',
  FORTIFIED: 'Fortified',
};

const FEED_EXPIRY_TYPE_REVERSE: Record<string, string> = {
  'Plain': 'PLAIN',
  'Fortified': 'FORTIFIED',
};

function toViewUnit(unit: string): ExpiryRuleEntry['expiryUnit'] {
  return unit === 'DAYS' || unit === 'MONTHS' ? 'days' : 'hours';
}

function toApiUnit(unit: ExpiryRuleEntry['expiryUnit']): string {
  return unit === 'days' ? 'DAYS' : 'HOURS';
}

export async function fetchExpiryRules(expiryType: 'MILK' | 'FEED' | 'FORMULA' = 'MILK'): Promise<ExpiryRuleEntry[]> {
  const response = await api.get<ExpiryRuleRow[]>(`${API_ROUTES.SETTINGS}/milkExpiryRules/`, {
    params: { expiry_type: expiryType },
  });
  // NOTE: inactive rows are intentionally kept (not filtered out) so opt-in
  // reference rows — e.g. the "Deep Frozen" state rows for Mother Milk/Donor
  // Milk/Donor Pasteurised Milk — can be surfaced behind a toggle in the UI.
  return response.data
    .map((row) => {
      const resolvedType = (row.expiry_type ?? expiryType) as 'MILK' | 'FEED' | 'FORMULA';
      const isFeed = resolvedType === 'FEED';
      return {
        id: row.rule_id,
        category: toViewCategory(row.category),
        milkType: row.milk_type_label ? (MILK_TYPE_LABEL_MAP[row.milk_type_label] ?? row.milk_type_label) : '',
        feedType: isFeed && row.feed_type ? (FEED_EXPIRY_TYPE_MAP[row.feed_type] ?? row.feed_type) : undefined,
        state: row.state ?? undefined,
        location: row.storage_condition ?? undefined,
        formFactor: row.form_factor ?? undefined,
        expiryType: resolvedType,
        expiryDuration: row.expiry_duration,
        expiryUnit: toViewUnit(row.unit),
        isActive: row.is_active,
      } satisfies ExpiryRuleEntry;
    });
}

export async function addExpiryRule(rule: Omit<ExpiryRuleEntry, 'id'>, expiryType: 'MILK' | 'FEED' | 'FORMULA' = 'MILK'): Promise<ExpiryRuleEntry> {
  const hospitalId = requireHospitalId();
  const isFeed = expiryType === 'FEED';
  const milkTypeLabel = MILK_TYPE_LABEL_REVERSE[rule.milkType] ?? 'FRESH';
  const response = await api.post<ExpiryRuleRow>(`${API_ROUTES.SETTINGS}/milkExpiryRules/`, {
    hospital_id: hospitalId,
    category: toApiCategory(rule.category),
    milk_type_label: milkTypeLabel,
    expiry_type: expiryType,
    ...(isFeed
      ? {
          feed_type: rule.feedType ? (FEED_EXPIRY_TYPE_REVERSE[rule.feedType] ?? 'PLAIN') : 'PLAIN',
          storage_condition: 'REFRIGERATED',
        }
      : {
          storage_condition: 'REFRIGERATED',
        }
    ),
    expiry_duration: rule.expiryDuration,
    unit: toApiUnit(rule.expiryUnit),
    is_active: true,
    created_by: config.createdBy,
  });
  const row = response.data;
  const rowIsFeed = (row.expiry_type ?? expiryType) === 'FEED';
  return {
    id: row.rule_id,
    category: toViewCategory(row.category),
    milkType: MILK_TYPE_LABEL_MAP[row.milk_type_label] ?? row.milk_type_label,
    feedType: rowIsFeed
      ? (row.feed_type ? (FEED_EXPIRY_TYPE_MAP[row.feed_type] ?? row.feed_type) : undefined)
      : (row.storage_condition ? (FEED_TYPE_MAP[row.storage_condition] ?? row.storage_condition) : undefined),
    expiryType: rowIsFeed ? 'FEED' : 'MILK',
    expiryDuration: row.expiry_duration,
    expiryUnit: toViewUnit(row.unit),
  };
}

export async function updateExpiryRule(rule: ExpiryRuleEntry): Promise<void> {
  // NOTE: only duration/unit are user-editable from the Expiry Rules screen —
  // category, milk_type_label, form_factor, state and storage_condition/
  // feed_type are intentionally NOT re-sent here so an edit can never
  // overwrite those reference-data classification fields (previously this
  // clobbered storage_condition/feed_type on every FEED-row duration edit).
  await api.put(`${API_ROUTES.SETTINGS}/milkExpiryRules/${rule.id}`, {
    expiry_duration: rule.expiryDuration,
    unit: toApiUnit(rule.expiryUnit),
    updated_by: config.createdBy,
  });
}

export async function deleteExpiryRule(id: string): Promise<void> {
  await api.delete(`${API_ROUTES.SETTINGS}/milkExpiryRules/${id}`);
}

/**
 * Activates/deactivates an existing milk expiry rule without touching any other
 * field. Used by the Deep Frozen toggle for Mother Milk/Donor Milk/Donor
 * Pasteurised Milk in the Milk Expiry Configuration table.
 */
export async function setExpiryRuleActive(id: string, isActive: boolean): Promise<void> {
  await api.put(`${API_ROUTES.SETTINGS}/milkExpiryRules/${id}`, {
    is_active: isActive,
    updated_by: config.createdBy,
  });
}

// ---------------------------------------------------------------------------
// Roles & Permissions (Step 5) — role-module-matrix via tms-api-onboarding
// ---------------------------------------------------------------------------

type ApiRoleKey =
  | 'NURSE'
  | 'PREP_TECHNICIAN'
  | 'NICU_MANAGER'
  | 'CLINICAL_ADMIN'
  | 'TECHNICAL_ADMIN'
  | 'ENTERPRISE_ADMIN'
  | 'INVENTORY_MANAGER'
  | 'INTAKE_STAFF'
  | 'MILK_BANK_OPERATOR'
  | 'DONOR_COORDINATOR'
  | 'DIETITIAN'
  | 'LACTATION'
  | 'MILK_PREP_TECHNICIAN'
  | 'UNIT_SECRETARY'
  | 'ANCILLARY_STAFF'
  | 'AUDITOR';
type ApiModuleKey = 'MILK_MANAGEMENT' | 'FEED_PREP' | 'ADMINISTRATION' | 'INVENTORY' | 'REPORTS' | 'SETTINGS' | 'DONOR_PORTAL' | 'RECALL' | 'PATIENT_MOTHER_DONOR_MGMT' | 'AUDIT_LOG' | 'USER_MANAGEMENT';

type RoleModuleMatrixRow = Record<ApiModuleKey, boolean>;
type RoleModuleMatrix = Record<ApiRoleKey, RoleModuleMatrixRow>;

type RoleModuleMatrixApiResponse = {
  hospital_id: string;
  matrix: RoleModuleMatrix;
  source: 'hospital' | 'default';
  updated_by?: string;
  updated_at?: string;
};

const ROLE_META: Record<ApiRoleKey, { roleName: string; roleSubtitle: string }> = {
  NURSE:                  { roleName: 'Nurse',                  roleSubtitle: 'Primary Care Giver'       },
  PREP_TECHNICIAN:        { roleName: 'Prep Technician',        roleSubtitle: 'Lab Operations'           },
  NICU_MANAGER:           { roleName: 'NICU Manager',           roleSubtitle: 'Unit Supervisor'          },
  CLINICAL_ADMIN:         { roleName: 'Clinical Admin',         roleSubtitle: 'Clinical Operations'      },
  TECHNICAL_ADMIN:        { roleName: 'Technical Admin',        roleSubtitle: 'System Administrator'     },
  ENTERPRISE_ADMIN:       { roleName: 'Enterprise Admin',       roleSubtitle: 'Global Controller'        },
  INVENTORY_MANAGER:      { roleName: 'Inventory Manager',      roleSubtitle: 'Stock & Supply'           },
  INTAKE_STAFF:           { roleName: 'Intake Staff',           roleSubtitle: 'Donor Intake Operations'  },
  MILK_BANK_OPERATOR:     { roleName: 'Milk Bank Operator',     roleSubtitle: 'Milk Bank Operations'     },
  DONOR_COORDINATOR:      { roleName: 'Donor Coordinator',      roleSubtitle: 'Donor Relations'          },
  DIETITIAN:              { roleName: 'Dietitian',              roleSubtitle: 'Nutritional Care'         },
  LACTATION:              { roleName: 'Lactation Consultant',   roleSubtitle: 'Lactation Support'        },
  MILK_PREP_TECHNICIAN:   { roleName: 'Milk Prep Technician',   roleSubtitle: 'Milk Preparation'         },
  UNIT_SECRETARY:         { roleName: 'Unit Secretary',         roleSubtitle: 'Administrative Support'   },
  ANCILLARY_STAFF:        { roleName: 'Ancillary Staff',        roleSubtitle: 'Support Services'         },
  AUDITOR:                { roleName: 'Auditor',                roleSubtitle: 'Audit & Compliance'       },
};

const API_ROLE_ORDER: ApiRoleKey[] = [
  'NURSE',
  'PREP_TECHNICIAN',
  'NICU_MANAGER',
  'CLINICAL_ADMIN',
  'TECHNICAL_ADMIN',
  'ENTERPRISE_ADMIN',
  'INVENTORY_MANAGER',
  'INTAKE_STAFF',
  'MILK_BANK_OPERATOR',
  'DONOR_COORDINATOR',
  'DIETITIAN',
  'LACTATION',
  'MILK_PREP_TECHNICIAN',
  'UNIT_SECRETARY',
  'ANCILLARY_STAFF',
  'AUDITOR',
];

function toUiPermissions(row: RoleModuleMatrixRow): RolePermissionEntry['permissions'] {
  return {
    milkManagement:         row.MILK_MANAGEMENT,
    feedPrep:               row.FEED_PREP,
    administration:         row.ADMINISTRATION,
    inventory:              row.INVENTORY,
    reports:                row.REPORTS,
    settings:               row.SETTINGS,
    donorPortal:            row.DONOR_PORTAL,
    recall:                 row.RECALL,
    patientMotherDonorMgmt: row.PATIENT_MOTHER_DONOR_MGMT,
    auditLog:               row.AUDIT_LOG,
    userManagement:         row.USER_MANAGEMENT,
  };
}

function toApiPermissions(p: RolePermissionEntry['permissions']): RoleModuleMatrixRow {
  return {
    MILK_MANAGEMENT:           p.milkManagement,
    FEED_PREP:                 p.feedPrep,
    ADMINISTRATION:            p.administration,
    INVENTORY:                 p.inventory,
    REPORTS:                   p.reports,
    SETTINGS:                  p.settings,
    DONOR_PORTAL:              p.donorPortal,
    RECALL:                    p.recall,
    PATIENT_MOTHER_DONOR_MGMT: p.patientMotherDonorMgmt,
    AUDIT_LOG:                 p.auditLog,
    USER_MANAGEMENT:           p.userManagement,
  };
}

export async function fetchRolesPermissions(): Promise<RolePermissionEntry[]> {
  const hospitalId = requireHospitalId();
  const response = await api.get<RoleModuleMatrixApiResponse>(
    `${API_ROUTES.ONBOARDING}/tenants/${hospitalId}/role-module-matrix/`,
  );
  const { matrix } = response.data;
  return API_ROLE_ORDER.map((roleKey) => ({
    id: roleKey,
    ...ROLE_META[roleKey],
    permissions: toUiPermissions(matrix[roleKey]),
  }));
}

export async function fetchRolesPermissionsDefaults(): Promise<RolePermissionEntry[]> {
  const hospitalId = requireHospitalId();
  const response = await api.get<RoleModuleMatrixApiResponse>(
    `${API_ROUTES.ONBOARDING}/tenants/${hospitalId}/role-module-matrix/defaults`,
  );
  const { matrix } = response.data;
  return API_ROLE_ORDER.map((roleKey) => ({
    id: roleKey,
    ...ROLE_META[roleKey],
    permissions: toUiPermissions(matrix[roleKey]),
  }));
}

export async function saveRolesPermissions(roles: RolePermissionEntry[]): Promise<void> {
  const hospitalId = requireHospitalId();
  const matrix = Object.fromEntries(
    roles.map((r) => [r.id as ApiRoleKey, toApiPermissions(r.permissions)]),
  ) as RoleModuleMatrix;
  await api.put(
    `${API_ROUTES.ONBOARDING}/tenants/${hospitalId}/role-module-matrix/`,
    { matrix, updated_by: config.createdBy },
  );
}

// ---------------------------------------------------------------------------
// Printer Configuration (Step 6)
// ---------------------------------------------------------------------------

function requireTenantId(): string {
  if (!config.tenantId) throw new Error('VITE_TENANT_ID is not configured');
  return config.tenantId;
}

export async function fetchDevices(deviceType?: string): Promise<Device[]> {
  const response = await api.get<Device[]>(
    `${API_ROUTES.SETTINGS}/devices/`,
    deviceType ? { params: { device_type: deviceType } } : undefined,
  );
  return response.data;
}

export async function updateDeviceCareUnit(deviceId: string, careUnitId: string): Promise<Device> {
  const response = await api.patch<Device>(
    `${API_ROUTES.SETTINGS}/devices/${deviceId}/careUnit`,
    { care_unit_id: careUnitId },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Approval & Compliance (Step 7)
// ---------------------------------------------------------------------------

export const BLOOD_TEST_OPTIONS = [
  'HIV',
  'Hepatitis B',
  'Hepatitis C',
  'HTLV',
  'VDRL/RPR (Syphilis)',
  'CMV (Cytomegalovirus)',
];

// ── IDP Federation constants ────────────────────────────────────────────────

export const IDP_PROTOCOL_OPTIONS = [
  { label: 'SAML 2.0', value: 'saml' },
  { label: 'OIDC / OAuth 2.0', value: 'oidc' },
];

export const IDP_PROVIDER_OPTIONS = [
  { label: 'Microsoft Azure AD / Entra ID', value: 'azure' },
  { label: 'Google Workspace', value: 'google' },
  { label: 'Okta', value: 'okta' },
  { label: 'Auth0', value: 'auth0' },
  { label: 'AWS IAM Identity Center', value: 'aws-sso' },
  { label: 'GCP Cloud Identity', value: 'gcp' },
  { label: 'Custom / Other', value: 'custom' },
];

/** Maps blood test display names to the API's settingKey values. */
const BLOOD_TEST_KEY_MAP: Record<string, string> = {
  'HIV':                   'donor.blood_test.hiv_validity_days',
  'Hepatitis B':           'donor.blood_test.hbsag_validity_days',
  'Hepatitis C':           'donor.blood_test.hcv_validity_days',
  'HTLV':                  'donor.blood_test.htlv_validity_days',
  'VDRL/RPR (Syphilis)':   'donor.blood_test.syphilis_validity_days',
  'CMV (Cytomegalovirus)': 'donor.blood_test.cmv_validity_days',
};

/** Reverse: API key → display name */
const BLOOD_TEST_NAME_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(BLOOD_TEST_KEY_MAP).map(([name, key]) => [key, name]),
);

function durationToDays(duration: number, unit: ValidityUnit): number {
  if (unit === 'Weeks') return duration * 7;
  if (unit === 'Months') return duration * 30;
  return duration;
}

function parseApprovalSettings(rows: ApprovalComplianceSetting[]): ApprovalComplianceFormData {
  const get = (key: string) => rows.find((r) => r.settingKey === key)?.settingValue;

  const safeInt = (raw: string | undefined, fallback: number): number => {
    const n = parseInt(raw ?? '', 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const bloodTests: BloodTestEntry[] = Object.entries(BLOOD_TEST_NAME_MAP).map(
    ([apiKey, testName], i) => ({
      id: `bt-${i}`,
      settingKey: apiKey,
      testName,
      duration: safeInt(get(apiKey), 90),
      unit: 'Days' as ValidityUnit,
    }),
  );

  const bottleIndexOf = (key: string) => safeInt(key.split('.').at(-1), 0);
  const bottleRows = rows
    .filter((r) => r.settingKey.startsWith('hospital.bottle_quantity.'))
    .sort((a, b) => bottleIndexOf(a.settingKey) - bottleIndexOf(b.settingKey));
  const bottleQuantities = bottleRows.length > 0
    ? bottleRows.map((r) => safeInt(r.settingValue, 100))
    : [100];
  const advance = get('hospital.advance_feed_preparation') ?? '24-hour';

  return {
    healthScreeningValidity: {
      duration: safeInt(get('donor.screening.validity_days'), 180),
      unit: 'Days',
    },
    donorBloodTests: bloodTests,
    feedPreparationCycle: advance === '12-hour' ? '12h' : '24h',
    bottleSizes: bottleQuantities.map((qty, i) => ({ id: `bs-${i}`, quantity: qty, unit: i18next.t('common.milkUnit') })),
    allowPreviousOrders: get('hospital.previous_feeding_orders') === 'true',
  };
}

export async function fetchApprovalCompliance(): Promise<ApprovalComplianceFormData> {
  try {
    const response = await api.get<ApprovalComplianceSetting[]>(
      `${API_ROUTES.SETTINGS}/approval-compliance/`,
    );
    return parseApprovalSettings(response.data);
  } catch (err) {
    // 404 means no settings saved yet — throw with a user-friendly message
    // so the view can show a warning while still loading defaults for first-time setup
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      const notConfiguredError = new Error(
        i18next.t('apiErrors.approvalComplianceNotConfigured'),
      );
      (notConfiguredError as Error & { isNotConfigured: boolean }).isNotConfigured = true;
      throw notConfiguredError;
    }
    throw err;
  }
}

/**
 * Returns the same default values that the API would return for a
 * tenant that has never saved Approval & Compliance settings.
 * Used by the wizard view to seed the form on first-time setup (404).
 */
export function defaultApprovalCompliance(): ApprovalComplianceFormData {
  return parseApprovalSettings([]);
}

export async function saveApprovalCompliance(data: ApprovalComplianceFormData): Promise<void> {
  const hospitalId = requireHospitalId();
  const tenantId = requireTenantId();
  const createdBy = config.createdBy;
  if (!createdBy) throw new Error('VITE_CREATED_BY is not configured');

  const getTestDays = (settingKey: string): number => {
    const entry = data.donorBloodTests.find((bt) => bt.settingKey === settingKey);
    return entry ? durationToDays(entry.duration, entry.unit) : 90;
  };

  await api.post(`${API_ROUTES.SETTINGS}/approval-compliance/`, {
    tenant_id: tenantId,
    hospital_id: hospitalId,
    created_by: createdBy,
    previous_feeding_orders: data.allowPreviousOrders,
    screening_validity_days: durationToDays(
      data.healthScreeningValidity.duration,
      data.healthScreeningValidity.unit,
    ),
    blood_test_hiv_validity_days:      getTestDays('donor.blood_test.hiv_validity_days'),
    blood_test_hbsag_validity_days:    getTestDays('donor.blood_test.hbsag_validity_days'),
    blood_test_hcv_validity_days:      getTestDays('donor.blood_test.hcv_validity_days'),
    blood_test_htlv_validity_days:     getTestDays('donor.blood_test.htlv_validity_days'),
    blood_test_cmv_validity_days:      getTestDays('donor.blood_test.cmv_validity_days'),
    blood_test_syphilis_validity_days: getTestDays('donor.blood_test.syphilis_validity_days'),
    bottle_quantities: data.bottleSizes.map((b) => b.quantity),
    advance_feed_preparation: data.feedPreparationCycle === '12h' ? '12-hour' : '24-hour',
  });
}

/**
 * POST /api/v1/patient/milkProviderHealthRecords/screening/re-evaluate
 *
 * Recomputes screening status for every donor of the current hospital. Called after
 * Approval & Compliance is saved, because changing a test's validity window changes
 * the verdict for donors whose serology has not changed at all.
 *
 * Idempotent. Returns how many donors were examined and how many actually changed.
 */
export async function reEvaluateDonorScreening(): Promise<{ evaluated: number; changed: number }> {
  const hospitalId = requireHospitalId();
  const response = await api.post<{ evaluated: number; changed: number }>(
    `${API_ROUTES.PATIENT}/milkProviderHealthRecords/screening/re-evaluate`,
    { hospital_id: hospitalId },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Milk Formula Storage (Step 9) — Storage Locations, Inventory Locations, Pasteurization Devices
// ---------------------------------------------------------------------------

type StorageLocationRow = {
  storage_location_id: string;
  name: string;
  storage_type: string;
  is_active: boolean;
};

type InventoryLocationRow = {
  location_id: string;
  name: string;
  location_type: string;
  is_active: boolean;
};

type PasteurizationDeviceRow = {
  pasteurization_id: string;
  name: string;
  is_active: boolean;
};

/**
 * Immediately create a single inventory location (formula storage) for the hospital.
 * Used by the Formula Storage Locations TagChipList to persist on add.
 */
export async function addInventoryLocation(name: string, locationType?: string): Promise<StorageLocationEntry> {
  const hospitalId = requireHospitalId();
  const response = await api.post<InventoryLocationRow>(`${API_ROUTES.SETTINGS}/inventory-locations/`, {
    hospital_id: hospitalId,
    name,
    ...(locationType ? { location_type: locationType } : {}),
  });
  return { id: response.data.location_id, label: response.data.name, type: response.data.location_type };
}

/** Deactivate an inventory location (milk). */
export async function removeInventoryLocation(id: string): Promise<void> {
  await api.patch(`${API_ROUTES.SETTINGS}/inventory-locations/${id}/status`, { is_active: false });
}

/** Immediately create a single storage location (milk storage) for the hospital. */
export async function addStorageLocation(name: string, storageType?: string): Promise<StorageLocationEntry> {
  const hospitalId = requireHospitalId();
  const response = await api.post<StorageLocationRow>(`${API_ROUTES.SETTINGS}/storage-locations/`, {
    hospital_id: hospitalId,
    name,
    ...(storageType ? { storage_type: storageType } : {}),
  });
  return { id: response.data.storage_location_id, label: response.data.name, type: response.data.storage_type };
}

/** Deactivate a storage location (formula). */
export async function removeStorageLocation(id: string): Promise<void> {
  await api.patch(`${API_ROUTES.SETTINGS}/storage-locations/${id}/status`, { is_active: false });
}

/** Immediately create a pasteurization device for the hospital. */
export async function addPasteurizationDevice(name: string): Promise<StorageLocationEntry> {
  const hospitalId = requireHospitalId();
  const response = await api.post<PasteurizationDeviceRow>(`${API_ROUTES.SETTINGS}/pasteurization-devices/`, {
    hospital_id: hospitalId,
    name,
  });
  return { id: response.data.pasteurization_id, label: response.data.name };
}

/** Deactivate a pasteurization device. */
export async function removePasteurizationDevice(id: string): Promise<void> {
  await api.patch(`${API_ROUTES.SETTINGS}/pasteurization-devices/${id}/status`, { is_active: false });
}

export async function fetchMilkFormulaStorage(includeFormula = true): Promise<MilkFormulaStorageFormData> {
  const [milkRes, formulaRes, pastRes] = await Promise.all([
    api.get<StorageLocationRow[]>(`${API_ROUTES.SETTINGS}/storage-locations/`),
    includeFormula
      ? api.get<InventoryLocationRow[]>(`${API_ROUTES.SETTINGS}/inventory-locations/`)
      : Promise.resolve({ data: [] as InventoryLocationRow[] }),
    api.get<PasteurizationDeviceRow[]>(`${API_ROUTES.SETTINGS}/pasteurization-devices/`),
  ]);
  return {
    milkLocations: milkRes.data.filter((r) => r.is_active).map((r) => ({ id: r.storage_location_id, label: r.name, type: r.storage_type })),
    formulaLocations: formulaRes.data.filter((r) => r.is_active).map((r) => ({ id: r.location_id, label: r.name, type: r.location_type })),
    pasteurizationDevices: pastRes.data.filter((r) => r.is_active).map((r) => ({ id: r.pasteurization_id, label: r.name })),
  };
}

export async function saveMilkFormulaStorage(
  original: MilkFormulaStorageFormData | null,
  updated: MilkFormulaStorageFormData,
  includeFormula = true,
): Promise<void> {
  const hospitalId = requireHospitalId();

  function diff(orig: StorageLocationEntry[], curr: StorageLocationEntry[]) {
    const origIds = new Set(orig.map((o) => o.id));
    const currIds = new Set(curr.map((c) => c.id));
    const added = curr.filter((c) => !origIds.has(c.id));
    const removed = orig.filter((o) => !currIds.has(o.id));
    return { added, removed };
  }

  const orig = original ?? { milkLocations: [], formulaLocations: [], pasteurizationDevices: [] };
  const milk = diff(orig.milkLocations, updated.milkLocations);
  const formula = includeFormula ? diff(orig.formulaLocations, updated.formulaLocations) : { added: [], removed: [] };
  const past = diff(orig.pasteurizationDevices, updated.pasteurizationDevices);

  await Promise.all([
    ...milk.added.map((l) =>
      api.post(`${API_ROUTES.SETTINGS}/storage-locations/`, {
        hospital_id: hospitalId,
        name: l.label,
        ...(l.type ? { storage_type: l.type } : {}),
      }),
    ),
    ...milk.removed.map((l) =>
      api.patch(`${API_ROUTES.SETTINGS}/storage-locations/${l.id}/status`, { is_active: false }),
    ),
    ...formula.added.map((l) =>
      api.post(`${API_ROUTES.SETTINGS}/inventory-locations/`, {
        hospital_id: hospitalId,
        name: l.label,
        ...(l.type ? { location_type: l.type } : {}),
      }),
    ),
    ...formula.removed.map((l) =>
      api.patch(`${API_ROUTES.SETTINGS}/inventory-locations/${l.id}/status`, { is_active: false }),
    ),
    ...past.added.map((l) =>
      api.post(`${API_ROUTES.SETTINGS}/pasteurization-devices/`, { hospital_id: hospitalId, name: l.label }),
    ),
    ...past.removed.map((l) =>
      api.patch(`${API_ROUTES.SETTINGS}/pasteurization-devices/${l.id}/status`, { is_active: false }),
    ),
  ]);
}

// ---------------------------------------------------------------------------
// IDP Federation (Step 10) — tms-api-onboarding :5008
// ---------------------------------------------------------------------------

export type CreateIdpPayload = {
  provider_name: string;
  provider_type: 'SAML' | 'OIDC';
  provider_details: Record<string, string>;
  attribute_mapping?: Record<string, string>;
  idp_identifiers?: string[];
};

export type IdpProviderResponse = {
  provider_name: string;
  provider_type: 'SAML' | 'OIDC';
  provider_details: Record<string, string>;
  attribute_mapping: Record<string, string>;
  idp_identifiers: string[];
  creation_date: string;
  last_modified_date: string;
};

/**
 * Create a SAML or OIDC identity provider in the tenant's Cognito User Pool.
 * POST /onboarding/api/v1/idps
 */
export async function createIdp(payload: CreateIdpPayload): Promise<IdpProviderResponse> {
  const tenantId = requireTenantId();
  const response = await api.post<IdpProviderResponse>(
    `${API_ROUTES.ONBOARDING}/idps`,
    { tenant_id: tenantId, ...payload },
  );
  return response.data;
}

/**
 * List all identity providers for the current tenant.
 * GET /onboarding/api/v1/idps
 */
export async function listIdps(): Promise<IdpProviderResponse[]> {
  const tenantId = requireTenantId();
  const response = await api.get<{ providers: IdpProviderResponse[] }>(
    `${API_ROUTES.ONBOARDING}/idps`,
    { params: { tenant_id: tenantId } },
  );
  return response.data.providers;
}

export type UpdateIdpPayload = {
  provider_type: 'SAML' | 'OIDC';
  provider_details?: Record<string, string>;
  attribute_mapping?: Record<string, string>;
  idp_identifiers?: string[];
};

/**
 * Get a single identity provider by name.
 * GET /onboarding/api/v1/idps/:provider_name
 */
export async function getIdp(providerName: string): Promise<IdpProviderResponse> {
  const tenantId = requireTenantId();
  const response = await api.get<IdpProviderResponse>(
    `${API_ROUTES.ONBOARDING}/idps/${encodeURIComponent(providerName)}`,
    { params: { tenant_id: tenantId } },
  );
  return response.data;
}

/**
 * Update an existing identity provider.
 * PUT /onboarding/api/v1/idps/:provider_name
 */
export async function updateIdp(
  providerName: string,
  payload: UpdateIdpPayload,
): Promise<IdpProviderResponse> {
  const tenantId = requireTenantId();
  const response = await api.put<IdpProviderResponse>(
    `${API_ROUTES.ONBOARDING}/idps/${encodeURIComponent(providerName)}`,
    { tenant_id: tenantId, ...payload },
  );
  return response.data;
}

/**
 * Delete an identity provider.
 * DELETE /onboarding/api/v1/idps/:provider_name
 */
export async function deleteIdp(providerName: string): Promise<void> {
  const tenantId = requireTenantId();
  await api.delete(
    `${API_ROUTES.ONBOARDING}/idps/${encodeURIComponent(providerName)}`,
    { params: { tenant_id: tenantId } },
  );
}

// ---------------------------------------------------------------------------
// IDP Linking — cross-pool IdP discovery and clone (tms-api-onboarding :5008)
// ---------------------------------------------------------------------------

export type PoolRef = {
  tenant_id: string;
  user_pool_id: string;
};

export type CrossPoolIdp = {
  provider_name: string;
  provider_type: 'SAML' | 'OIDC';
  exists_in_pools: PoolRef[];
};

export type ListAllIdpsResponse = {
  providers: CrossPoolIdp[];
  total_pools_scanned: number;
};

/**
 * List all identity providers across every active tenant's Cognito pool.
 * GET /onboarding/api/v1/idps/all
 */
export async function listAllIdps(): Promise<ListAllIdpsResponse> {
  const response = await api.get<ListAllIdpsResponse>(
    `${API_ROUTES.ONBOARDING}/idps/all`,
  );
  return response.data;
}

export type CloneIdpPayload = {
  source_tenant_id: string;
  target_tenant_id: string;
  attribute_mapping?: Record<string, string>;
  idp_identifiers?: string[];
};

/**
 * Clone an identity provider from a source tenant pool into the target tenant pool.
 * POST /onboarding/api/v1/idps/{provider_name}/clone
 */
export async function cloneIdp(
  providerName: string,
  payload: CloneIdpPayload,
): Promise<IdpProviderResponse> {
  const response = await api.post<IdpProviderResponse>(
    `${API_ROUTES.ONBOARDING}/idps/${encodeURIComponent(providerName)}/clone`,
    payload,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// IDP SP Details — Cognito Service Provider configuration
// ---------------------------------------------------------------------------

export type SpRequiredAttribute = {
  name: string;
  required: boolean;
  description: string;
};

export type SpDetailsResponse = {
  acs_url: string;
  sp_entity_id: string;
  audience_uri: string;
  audience_restriction: string;
  sp_metadata_url: string;
  cognito_domain: string;
  region: string;
  required_attributes: SpRequiredAttribute[];
};

/**
 * Fetch the tenant's Cognito SP (Service Provider) details for IDP configuration.
 * GET /onboarding/api/v1/tenant-config/sp-details?domain=<hostname>
 */
export async function fetchSpDetails(): Promise<SpDetailsResponse> {

  const domain = window.location.hostname;
  const response = await api.get<SpDetailsResponse>(
    `${API_ROUTES.ONBOARDING}/tenant-config/sp-details`,
    { params: { domain } },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Tenant Settings — first-time configuration flag
// ---------------------------------------------------------------------------

type SettingsConfiguredResponse = {
  hospital_id: string;
  is_settings_configured: boolean;
  updated_at?: string;
};

/**
 * Check whether the tenant's settings have been configured.
 * GET /api/v1/tenant-settings/settings-configured
 * Returns true if the tenant has completed first-time setup, false otherwise.
 */
export async function checkSettingsConfigured(): Promise<boolean> {
  const response = await api.get<SettingsConfiguredResponse>(
    `${API_ROUTES.SETTINGS}/tenant-settings/settings-configured`,
  );
  return response.data.is_settings_configured;
}

/**
 * Mark the tenant's settings as fully configured.
 * PUT /api/v1/tenant-settings/settings-configured
 * Called after the user completes the last step of the settings wizard.
 */
export async function markSettingsConfigured(): Promise<void> {
  await api.put(
    `${API_ROUTES.SETTINGS}/tenant-settings/settings-configured`,
    { is_settings_configured: true },
  );
}

type TenantHospitalNameResponse = {
  hospital_id: string;
  hospital_name: string;
};

/**
 * Get the hospital display name for the current tenant.
 * GET /api/v1/tenant-settings/hospital-name
 */
export async function getTenantHospitalName(): Promise<string> {
  const response = await api.get<TenantHospitalNameResponse>(
    `${API_ROUTES.SETTINGS}/tenant-settings/hospital-name`,
  );
  return response.data.hospital_name;
}

type OnboardingProgressResponse = {
  hospital_id: string;
  completed_steps: string[];
  started: boolean;
  updated_at?: string;
};

/**
 * Fetch which settings-wizard steps have already been completed (and whether
 * onboarding has been started) for this tenant, so first-time setup resumes
 * correctly on a new browser/device.
 * GET /api/v1/tenant-settings/onboarding-progress
 */
export async function fetchOnboardingProgress(): Promise<OnboardingProgressResponse> {
  const response = await api.get<OnboardingProgressResponse>(
    `${API_ROUTES.SETTINGS}/tenant-settings/onboarding-progress`,
  );
  return response.data;
}

/**
 * Update onboarding progress for this tenant: mark a single settings-wizard
 * step complete and/or flip the "onboarding started" flag.
 * PATCH /api/v1/tenant-settings/onboarding-progress
 * Best-effort — callers should treat failures as non-fatal since localStorage
 * remains the source of truth for the current browser session.
 */
export async function addOnboardingStepApi(updates: { stepKey?: string; started?: boolean }): Promise<OnboardingProgressResponse> {
  const response = await api.patch<OnboardingProgressResponse>(
    `${API_ROUTES.SETTINGS}/tenant-settings/onboarding-progress`,
    { step_key: updates.stepKey, started: updates.started },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Contact Details (Hospital Configuration) — support fields from tenant-config
// ---------------------------------------------------------------------------

type TenantConfigContactFields = {
  support_email_id?: string | null;
  support_contact_name?: string | null;
  support_facility_name?: string | null;
  support_contact_phone?: string | null;
  /**
   * The tenant's term for the medical record number, e.g. "EKB" for Bielefeld.
   *
   * Holds a LABEL, not a value prefix — it changes what the field is *called*,
   * never what is stored in it. The name is historical: the setting used to
   * prepend this to MRN values, which produced identifiers like "EKB2608444763"
   * that no longer matched the hospital feed. The prefixing was removed in
   * SV-334; the attribute was kept because tenants had already entered exactly
   * the term they wanted, so no data migration was needed. Renaming it is a
   * separate ticket.
   */
  mrn_prefix?: string | null;
};

/**
 * Fetch contact details for the current hospital.
 * GET /settings/api/v1/tenant-settings/support-email
 *
 * NOTE: previously this read by hospital_id (the same identifier
 * saveContactDetails() writes by) because the onboarding tenant-config
 * endpoint resolves tenants via a hostname scan, which could return a
 * different tenant record than the authenticated hospital when multiple
 * tenants share the same hostname (e.g. shared dev/QA domains) — causing
 * stale/incorrect values (like mrn_prefix) to be displayed. hospital_id is
 * no longer sent from the frontend; the backend now resolves the tenant
 * from the authenticated request context instead.
 */
export async function fetchContactDetails(): Promise<ContactDetailsFormData> {
  const response = await api.get<TenantConfigContactFields>(
    `${API_ROUTES.SETTINGS}/tenant-settings/support-email`,
  );
  const data = response.data;
  return {
    facilityName: data.support_facility_name ?? '',
    contactPerson: data.support_contact_name ?? '',
    contactPhone: data.support_contact_phone ?? '',
    contactEmail: data.support_email_id ?? '',
    mrnPrefix: data.mrn_prefix ?? '',
  };
}

/**
 * Persist contact details for the current hospital.
 * PUT /api/v1/tenant-settings/support-email
 */
export async function saveContactDetails(data: ContactDetailsFormData): Promise<void> {
  await api.put(
    `${API_ROUTES.SETTINGS}/tenant-settings/support-email`,
    {
      support_email_id: data.contactEmail || null,
      support_contact_name: data.contactPerson || null,
      support_facility_name: data.facilityName || null,
      support_contact_phone: data.contactPhone || null,
      // Sent verbatim — this is the display label, not a prefix. Null when blank,
      // which makes the UI fall back to the per-language default term.
      mrn_prefix: data.mrnPrefix || null,
    },
  );
}

// ---------------------------------------------------------------------------
// BridgeLink — certificates/user guide + connection status (tms-api-settings)
// ---------------------------------------------------------------------------

/**
 * Fetch BridgeLink certificate files and user guide download links.
 * GET /api/v1/bridgelink/assets
 */
export async function fetchBridgelinkAssets(): Promise<BridgelinkAssetsResponse> {
  const response = await api.get<BridgelinkAssetsResponse>(
    `${API_ROUTES.SETTINGS}/bridgelink/assets`,
  );
  return response.data;
}

/**
 * Fetch the current BridgeLink connection status for the tenant.
 * GET /api/v1/tenant-settings/bridgelink-status
 */
export async function fetchBridgelinkStatus(): Promise<BridgelinkStatusResponse> {
  const response = await api.get<BridgelinkStatusResponse>(
    `${API_ROUTES.SETTINGS}/tenant-settings/bridgelink-status`,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Profile Settings — per-hospital field settings for recipient / order profiles
// ---------------------------------------------------------------------------

/**
 * Fetch the profile setting of the given type for the caller's hospital.
 * GET /api/v1/app-profile-settings/:type
 * Rejects with an axios error; a 404 means it has not been saved yet.
 */
export async function fetchAppProfileSetting(
  type: AppProfileSettingType,
): Promise<AppProfileSettingResponse> {
  const response = await api.get<AppProfileSettingResponse>(
    `${API_ROUTES.SETTINGS}/app-profile-settings/${toAppProfileSettingSlug(type)}`,
  );
  return response.data;
}

/**
 * Create the profile setting of the given type.
 * POST /api/v1/app-profile-settings/:type — a 409 means it already exists.
 */
export async function createAppProfileSetting(
  type: AppProfileSettingType,
  columnDefinition: AppProfileSettingColumnDefinition,
): Promise<AppProfileSettingResponse> {
  const response = await api.post<AppProfileSettingResponse>(
    `${API_ROUTES.SETTINGS}/app-profile-settings/${toAppProfileSettingSlug(type)}`,
    { columnDefinition },
  );
  return response.data;
}

/**
 * Update the profile setting of the given type.
 * PUT /api/v1/app-profile-settings/:type — a 404 means it does not exist yet.
 */
export async function updateAppProfileSetting(
  type: AppProfileSettingType,
  columnDefinition: AppProfileSettingColumnDefinition,
): Promise<AppProfileSettingResponse> {
  const response = await api.put<AppProfileSettingResponse>(
    `${API_ROUTES.SETTINGS}/app-profile-settings/${toAppProfileSettingSlug(type)}`,
    { columnDefinition },
  );
  return response.data;
}

/**
 * Fetch the effective "Is Required" flags of the given type for the caller's hospital.
 * GET /api/v1/app-profile-settings/:type/effective — open to any authenticated user.
 * A flag is `true` (required) when no setting has been saved.
 */
export async function fetchEffectiveAppProfileSetting(
  type: AppProfileSettingType,
): Promise<AppProfileSettingEffectiveResponse> {
  const response = await api.get<AppProfileSettingEffectiveResponse>(
    `${API_ROUTES.SETTINGS}/app-profile-settings/${toAppProfileSettingSlug(type)}/effective`,
  );
  return response.data;
}
